import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { Option } from 'watchtower-ui/components/Filter/Inputs/MultiSelectInput';
import type { PodRuleForm } from 'watchtower-ui/pages/OptimizationPages/podOptimizer/podOptimizerForm';
import type {
  PodOptimizationMode,
  PodOptimizationResult,
} from 'watchtower-ui/pages/OptimizationPages/podOptimizer/podOptimizerTypes';

export type PodOptimizerCsvPreviewRow = Record<string, unknown>;

export interface PodOptimizerFormState {
  selectedBrandOverride: Option[] | null;
  periodRangeTimestamps: [number, number] | null;
  mode: PodOptimizationMode;
  targetPods: string;
  ruleOverride: PodRuleForm[] | null;
  uploadedCsvData: PodOptimizerCsvPreviewRow[] | null;
  result: PodOptimizationResult | null;
  searchTerm: string;
  csvValidationMessages: string[];
}

export const createInitialPodOptimizerFormState = (): PodOptimizerFormState => ({
  selectedBrandOverride: null,
  periodRangeTimestamps: null,
  mode: 'gain',
  targetPods: '10000',
  ruleOverride: null,
  uploadedCsvData: null,
  result: null,
  searchTerm: '',
  csvValidationMessages: [],
});

const podOptimizerSlice = createSlice({
  name: 'podOptimizer',
  initialState: createInitialPodOptimizerFormState(),
  reducers: {
    selectedBrandsChanged: (state, action: PayloadAction<{ value: Option[]; rules: PodRuleForm[] }>) => {
      state.selectedBrandOverride = action.payload.value;
      state.ruleOverride = action.payload.rules;
    },
    periodRangeChanged: (state, action: PayloadAction<[number, number]>) => {
      state.periodRangeTimestamps = action.payload;
    },
    modeChanged: (state, action: PayloadAction<PodOptimizationMode>) => {
      state.mode = action.payload;
    },
    targetPodsChanged: (state, action: PayloadAction<string>) => {
      state.targetPods = action.payload;
    },
    rulesChanged: (state, action: PayloadAction<PodRuleForm[]>) => {
      state.ruleOverride = action.payload;
    },
    csvRulesUploaded: (
      state,
      action: PayloadAction<{ rules: PodRuleForm[]; brands: Option[]; rows: PodOptimizerCsvPreviewRow[] }>,
    ) => {
      state.ruleOverride = action.payload.rules;
      state.selectedBrandOverride =
        action.payload.brands.length > 0 ? action.payload.brands : state.selectedBrandOverride;
      state.csvValidationMessages = [];
      state.uploadedCsvData = action.payload.rows;
    },
    csvValidationMessagesChanged: (state, action: PayloadAction<string[]>) => {
      state.csvValidationMessages = action.payload;
    },
    csvPreviewReset: (state) => {
      state.ruleOverride = null;
      state.csvValidationMessages = [];
      state.uploadedCsvData = null;
    },
    optimizationCompleted: (state, action: PayloadAction<PodOptimizationResult>) => {
      state.result = action.payload;
      state.searchTerm = '';
    },
    optimizationResultsCleared: (state) => {
      state.result = null;
    },
    searchTermChanged: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    podOptimizerFormRestored: (_state, action: PayloadAction<PodOptimizerFormState>) => action.payload,
    podOptimizerFormReset: () => createInitialPodOptimizerFormState(),
  },
});

export const {
  csvPreviewReset,
  csvRulesUploaded,
  csvValidationMessagesChanged,
  modeChanged,
  optimizationCompleted,
  optimizationResultsCleared,
  periodRangeChanged,
  podOptimizerFormReset,
  podOptimizerFormRestored,
  rulesChanged,
  searchTermChanged,
  selectedBrandsChanged,
  targetPodsChanged,
} = podOptimizerSlice.actions;

export default podOptimizerSlice.reducer;
